import { RouterView } from 'vue-router';
const EditCompanyLegalData = () => import('@/pages/accounting/company/EditCompanyLegalData.vue');
const AccountingBalances = () => import('@/pages/accounting/balances/BalanceAdjustments.vue');
const AccountingPatients = () => import('@/pages/accounting/AccountingPatients.vue');
const PatientInvoices = () => import('@/pages/accounting/invoices/PatientInvoices.vue');
const AccountingCompensations = () =>
    import('@/pages/accounting/refunds/AccountingCompensations.vue');
const ExternalProviders = () =>
    import('@/pages/accounting/externalProviders/ExternalProviders.vue');
const ProviderPayroll = () => import('@/pages/accounting/externalProviders/ProviderPayroll.vue');
const ProviderPayrollPeriodDetail = () =>
    import('@/pages/accounting/externalProviders/ProviderPayrollPeriodDetail.vue');
const ProviderPayrollDraft = () =>
    import('@/pages/accounting/externalProviders/ProviderPayrollDraft.vue');
const ProviderPayrollReview = () =>
    import('@/pages/accounting/externalProviders/ProviderPayrollReview.vue');
const AllAppointments = () => import('@/pages/frontdesk/AllAppointments.vue');
const LockedPatients = () =>
    import('@/pages/accounting/lockedPatient/chargebacks/LockedPatients.vue');
const ExternalProvidersCategories = () =>
    import('@/pages/accounting/externalProviders/ExternalProvidersCategories.vue');
const AccountingReports = () => import('@/pages/accounting/reports/AccountingReports.vue');
const QuoteLogs = () => import('@/pages/accounting/QuoteLogs.vue');
const AllQuoteTransfers = () => import('@/pages/sales/quoteTransfers/AllQuoteTransfers.vue');
const AllTreatmentTransfers = () =>
    import('@/pages/frontdesk/treatmentTransfers/AllTreatmentTransfers.vue');
const CheckOutSurgeryAppointment = () =>
    import('@/pages/medicalServices/surgeries/checkOut/CheckOutSurgeryAppointment.vue');
const accountingRoutes = [
    {
        path: 'accounting',
        name: 'accounting',
        redirect: (_to) => {
            return { path: '/accounting/providers' };
        },
        component: RouterView,
        meta: {
            title: 'Accounting',
            public: false,
            icon: 'CurrencyDollarIcon',
        },
        children: [
            {
                path: 'providers',
                name: 'AccountingExternalProviders',
                component: ExternalProviders,
                meta: {
                    menu: true,
                    title: 'ExternalProviders',
                    permissionsAllOf: [
                        'Permissions.Accounting.ExternalProvider.Search',
                        'Permissions.Accounting.ExternalProvider.Detail',
                    ],
                },
            },
            {
                // US #1803: User-facing label is "Provider Payroll". The URL segment
                // /provider-payroll is intentionally preserved for stable bookmarks.
                path: 'provider-payroll',
                name: 'ProviderPayroll',
                component: ProviderPayroll,
                meta: {
                    menu: true,
                    title: 'ProviderPayroll',
                    permissionsAnyOf: [
                        'Permissions.Accounting.ExternalProviderCheck.Create',
                        'Permissions.Accounting.ExternalProviderCheck.History',
                        'Permissions.Accounting.ExternalProviderCheck.Update',
                        'Permissions.Accounting.ExternalProviderCheck.Delete',
                        'Permissions.Accounting.ExternalProviderCheck.SendToReview',
                        'Permissions.Accounting.ExternalProviderCheck.ReturnToDraft',
                    ],
                },
            },
            {
                // US #1815: Parent shell for the selected provider payroll period.
                // Draft and Review become peer child tabs rendered inside the shell's
                // <RouterView>, matching the HR Payroll workflow. URLs and route
                // names for the children are preserved unchanged so deep links and
                // bookmarks keep working.
                path: 'provider-payroll/:id',
                name: 'ProviderPayrollPeriodDetail',
                component: ProviderPayrollPeriodDetail,
                props: true,
                // Bare /:id redirects to Draft, preserving params and query so
                // ?payDate=<iso> propagated from the payroll list survives the
                // redirect into the active tab.
                redirect: (to) => ({
                    name: 'ProviderPayrollDraft',
                    params: to.params,
                    query: to.query,
                }),
                meta: {
                    menu: false,
                    title: 'providerPayrollPeriodDetail',
                    // Shell guard — union of every permission required by either
                    // child tab plus ProviderPayrollPeriod.List so any user who can
                    // see at least one tab can reach the shell. Child route guards
                    // remain the true security boundary for each tab's content.
                    permissionsAnyOf: [
                        'Permissions.Accounting.ExternalProviderCheck.Create',
                        'Permissions.Accounting.ExternalProviderCheck.History',
                        'Permissions.Accounting.ExternalProviderCheck.Update',
                        'Permissions.Accounting.ExternalProviderCheck.Delete',
                        'Permissions.Accounting.ExternalProviderCheck.SendToReview',
                        'Permissions.Accounting.ExternalProviderCheck.ReturnToDraft',
                        'Permissions.Accounting.ProviderPayrollPeriod.List',
                    ],
                },
                children: [
                    {
                        path: 'draft',
                        name: 'ProviderPayrollDraft',
                        component: ProviderPayrollDraft,
                        props: true,
                        meta: {
                            menu: false,
                            title: 'draftChecklist',
                            // Allow the same broad set as ProviderPayroll so non-Delete users
                            // can inspect the draft in read-only mode. Destructive/mutating actions
                            // inside the page are individually guarded by their own AuthorizeView.
                            permissionsAnyOf: [
                                'Permissions.Accounting.ExternalProviderCheck.Create',
                                'Permissions.Accounting.ExternalProviderCheck.History',
                                'Permissions.Accounting.ExternalProviderCheck.Update',
                                'Permissions.Accounting.ExternalProviderCheck.Delete',
                                'Permissions.Accounting.ExternalProviderCheck.SendToReview',
                                'Permissions.Accounting.ExternalProviderCheck.ReturnToDraft',
                            ],
                        },
                    },
                    {
                        path: 'review',
                        name: 'ProviderPayrollReview',
                        component: ProviderPayrollReview,
                        props: true,
                        meta: {
                            menu: false,
                            title: 'reviewChecklist',
                            permissionsAllOf: ['Permissions.Accounting.ProviderPayrollPeriod.List'],
                        },
                    },
                ],
            },
            {
                path: 'external-providers-categories',
                name: 'ExternalProvidersCategories',
                component: ExternalProvidersCategories,
                meta: {
                    menu: true,
                    title: 'ExternalProvidersCategories',
                    permissionsAnyOf: ['Permissions.Accounting.ExternalProviderCategory.Search'],
                },
            },
            {
                path: 'company-legal-data',
                name: 'AccountingEditCompanyLegalData',
                component: EditCompanyLegalData,
                meta: {
                    menu: true,
                    title: 'Company',
                    permissionsAllOf: ['Permissions.Accounting.CompanyLegalData.Update'],
                },
            },
            {
                path: 'balances',
                name: 'AccountingBalances',
                component: AccountingBalances,
                meta: {
                    menu: true,
                    title: 'Balances',
                    permissionsAnyOf: ['Permissions.Accounting.BalanceAdjustments.Search'],
                },
            },
            {
                path: 'patients',
                name: 'AccountingPatients',
                component: AccountingPatients,
                meta: {
                    menu: true,
                    title: 'Patients',
                    permissionsAllOf: [
                        'Permissions.Accounting.LeadProfiles.Search',
                        'Permissions.Accounting.BalanceAdjustments.Search',
                    ],
                },
            },
            {
                path: 'invoice',
                name: 'PatientInvoices',
                component: PatientInvoices,
                meta: {
                    menu: true,
                    title: 'Invoices',
                    permissionsAnyOf: ['Permissions.Accounting.FinishedService.Search'],
                },
            },
            {
                path: 'quoteLogs',
                name: 'QuoteLogs',
                component: QuoteLogs,
                meta: {
                    menu: true,
                    title: 'quoteLogs',
                    permissionsAllOf: ['Permissions.Sales.Quotes.Logs'],
                },
            },
            {
                path: 'refunds',
                name: 'AccountingCompensations',
                component: AccountingCompensations,
                meta: {
                    menu: true,
                    title: 'Refunds',
                    permissionsAnyOf: ['Permissions.Accounting.Refund.Search'],
                },
            },
            {
                path: 'appointments',
                name: 'AllAccountingAppointments',
                component: AllAppointments,
                meta: {
                    menu: true,
                    title: 'All Appointments',
                    permissionsAllOf: ['Permissions.Accounting.Appointments.Search'],
                },
                props: {
                    checkOutRoute: 'AccountingChangeCheckOutSurgeryAppointment',
                },
            },
            {
                path: 'reports',
                name: 'AccountingReports',
                component: AccountingReports,
                meta: {
                    menu: true,
                    title: 'Reports',
                    permissionsAnyOf: ['Permissions.Reports.ExternalProvidersReport.View'],
                },
            },
            {
                path: 'chargebacks',
                name: 'Chargebacks',
                component: LockedPatients,
                meta: {
                    menu: true,
                    title: 'Chargebacks',
                    permissionsAnyOf: ['Permissions.Accounting.LockedPatient.Search'],
                },
            },
            {
                path: 'quote-transfer',
                name: 'QuoteTransfers',
                component: AllQuoteTransfers,
                meta: {
                    menu: true,
                    title: 'QuoteTransfers',
                    permissionsAllOf: ['Permissions.Sales.QuoteTransfers.Search'],
                },
            },
            {
                path: 'treatment-transfer',
                name: 'TreatmentTransfers',
                component: AllTreatmentTransfers,
                meta: {
                    menu: true,
                    title: 'TreatmentTransfers',
                    permissionsAllOf: ['Permissions.FrontDesk.TreatmentTransfers.Search'],
                },
            },
            {
                path: 'appointments/change-check-out/:id/:patientId',
                name: 'AccountingChangeCheckOutSurgeryAppointment',
                component: CheckOutSurgeryAppointment,
                meta: {
                    public: false,
                    title: 'CheckOut',
                    header: true,
                },
                props: {
                    editable: true,
                    route: 'AllAccountingAppointments',
                },
            },
        ],
    },
];

export default accountingRoutes;
